import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Zap, TrendingUp, ArrowRight, ArrowLeft, Calculator, Sparkles, Calendar, CheckCircle2 } from "lucide-react";
import { getStoredUtm } from "@/lib/utm";

const ROOF_TYPES = [
  { id: "asphalt", label: "Asphalt Shingle", efficiency: 1.0 },
  { id: "concrete", label: "Concrete Tile", efficiency: 0.98 },
  { id: "metal", label: "Metal Roof", efficiency: 1.05 },
  { id: "flat", label: "Flat Roof", efficiency: 0.95 },
];

const PROPERTY_TYPES = [
  { id: "single_family", label: "Single Family Home" },
  { id: "multi_family", label: "Multi-Family" },
  { id: "condo", label: "Apartment / Condo" },
  { id: "commercial", label: "Commercial" },
];

// Recommend package based on monthly bill (no public pricing exposed in the UI)
function recommendPackage(monthlyBill) {
  if (monthlyBill < 200) return { name: "Starter Solar", size: 3.8, key: "starter", tier: "Entry tier" };
  if (monthlyBill < 400) return { name: "Family Energy Saver", size: 6.4, key: "family", tier: "Mid tier" };
  return { name: "Premium Solar + Battery", size: 9.6, key: "premium", tier: "Top tier" };
}

export default function SolarCalculator({ embedded = false }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [bill, setBill] = useState(250);
  const [roof, setRoof] = useState("asphalt");
  const [property, setProperty] = useState("single_family");

  const result = useMemo(() => {
    const roofObj = ROOF_TYPES.find((r) => r.id === roof) || ROOF_TYPES[0];
    const offsetRate = 0.82; // 82% offset typical for SXM (high sun hours)
    const annualBill = bill * 12;
    const annualSavings = Math.round(annualBill * offsetRate * roofObj.efficiency);
    const lifetimeSavings = annualSavings * 25;
    const pkg = recommendPackage(bill);
    const co2OffsetTons = Math.round((annualSavings / 100) * 1.2); // rough kg CO2 / kWh proxy
    return { annualBill, annualSavings, lifetimeSavings, pkg, co2OffsetTons };
  }, [bill, roof]);

  const goToSignup = () => {
    // Persist calculator inputs so the consultation form can pre-fill and admin can see the numbers
    const utm = getStoredUtm();
    localStorage.setItem(
      "pb_calc_intent",
      JSON.stringify({
        bill, roof, property,
        recommended: result.pkg.key,
        annual_savings: result.annualSavings,
        lifetime_savings: result.lifetimeSavings,
        co2_offset_tons: result.co2OffsetTons,
        utm,
        ts: Date.now(),
      })
    );
    if (window.location.pathname.startsWith("/dashboard")) {
      navigate("/dashboard/services");
    } else {
      navigate("/login");
    }
  };

  return (
    <section id="calculator" className={`${embedded ? "py-0" : "py-24 bg-gradient-to-br from-[#F8FAFC] via-white to-[#F3E8CE]/40 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800"} relative overflow-hidden`}>
      {/* Decorative blobs */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#C9A356]/10 blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#0A2540]/5 blur-3xl"></div>

      <div className="max-w-6xl mx-auto px-6 relative">
        <div className={`grid ${embedded ? "lg:grid-cols-5" : "md:grid-cols-2"} gap-12 items-start`}>
          {/* Left: Pitch */}
          <div className={`${embedded ? "lg:col-span-2" : ""} md:sticky md:top-28`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A356]/15 rounded-full mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#C9A356]" />
              <span className="overline">3 quick questions</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#0A2540] dark:text-white tracking-tight leading-[1.1]">
              Tell us about<br />
              <span className="text-[#C9A356]">your home.</span>
            </h2>
            <p className="mt-5 text-[#475569] dark:text-slate-400 leading-relaxed text-lg">
              A few quick details so we can prepare a personalized estimate. We'll review the numbers with you in person at a free consultation — including projected savings, system sizing, and pricing options.
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: Sparkles, title: "Tailored to your home", body: "Roof type, property and energy use" },
                { icon: Calendar, title: "We'll discuss it together", body: "30-45 min consultation, free of charge" },
                { icon: Zap, title: "Zero pressure", body: "You choose what to do with the proposal" },
              ].map((f, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div className="w-9 h-9 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded-md grid place-items-center shrink-0">
                    <f.icon className="w-4 h-4 text-[#C9A356]" />
                  </div>
                  <div>
                    <div className="font-medium text-[#0A2540] dark:text-white text-sm">{f.title}</div>
                    <div className="text-sm text-[#475569] dark:text-slate-400">{f.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Calculator card */}
          <div className={`${embedded ? "lg:col-span-3" : ""} card-pb p-8 md:p-10 shadow-md relative`} data-testid="solar-calculator">
            <div className="flex items-center gap-2 text-xs text-[#475569] dark:text-slate-400 mb-4">
              <Calculator className="w-4 h-4 text-[#C9A356]" />
              <span>Step {Math.min(step, 4)} of 4</span>
              <div className="flex-1 ml-2 h-1 bg-slate-100 dark:bg-slate-700 rounded-full">
                <div className="h-1 bg-[#C9A356] rounded-full transition-all" style={{ width: `${(Math.min(step, 4) / 4) * 100}%` }}></div>
              </div>
            </div>

            {step === 1 && (
              <div data-testid="calc-step-1">
                <h3 className="font-heading text-2xl text-[#0A2540] dark:text-white font-medium">What's your average monthly electricity bill?</h3>
                <p className="text-sm text-[#475569] dark:text-slate-400 mt-2 mb-8">Drag the slider or type below. (USD)</p>

                <div className="bg-[#0A2540] dark:bg-slate-800 rounded-xl p-6 text-white text-center mb-6 border border-transparent dark:border-slate-700">
                  <p className="overline text-[#C9A356]">Monthly bill</p>
                  <div className="text-5xl font-heading font-bold mt-2" data-testid="calc-bill-display">${bill}</div>
                </div>

                <input
                  type="range" min="50" max="1500" step="10"
                  value={bill}
                  onChange={(e) => setBill(parseInt(e.target.value))}
                  data-testid="calc-bill-slider"
                  className="w-full accent-[#C9A356]"
                />
                <div className="flex justify-between text-xs text-[#475569] dark:text-slate-400 mt-1 mb-6">
                  <span>$50</span><span>$1,500+</span>
                </div>

                <input
                  type="number" value={bill}
                  onChange={(e) => setBill(Math.max(50, parseInt(e.target.value) || 50))}
                  data-testid="calc-bill-input"
                  className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356] outline-none mb-6"
                />

                <button onClick={() => setStep(2)} className="btn-accent w-full" data-testid="calc-next-1">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="calc-step-2">
                <h3 className="font-heading text-2xl text-[#0A2540] dark:text-white font-medium">What's your roof type?</h3>
                <p className="text-sm text-[#475569] dark:text-slate-400 mt-2 mb-6">This affects panel mounting & efficiency.</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {ROOF_TYPES.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setRoof(r.id)}
                      data-testid={`calc-roof-${r.id}`}
                      className={`text-left border-2 rounded-md p-4 transition-all ${
                        roof === r.id
                          ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-[#3a2e16]"
                          : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#0A2540] dark:hover:border-[#C9A356]"
                      }`}
                    >
                      <div className="font-medium text-[#0A2540] dark:text-white">{r.label}</div>
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(1)} className="btn-outline-navy flex-1" data-testid="calc-back-2">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </button>
                  <button onClick={() => setStep(3)} className="btn-accent flex-1" data-testid="calc-next-2">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="calc-step-3">
                <h3 className="font-heading text-2xl text-[#0A2540] dark:text-white font-medium">Property type?</h3>
                <p className="text-sm text-[#475569] dark:text-slate-400 mt-2 mb-6">Helps us recommend the right system.</p>
                <div className="space-y-2 mb-6">
                  {PROPERTY_TYPES.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setProperty(p.id)}
                      data-testid={`calc-property-${p.id}`}
                      className={`w-full text-left flex items-center justify-between border-2 rounded-md p-3 transition-all ${
                        property === p.id
                          ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-[#3a2e16]"
                          : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#0A2540] dark:hover:border-[#C9A356]"
                      }`}
                    >
                      <span className="font-medium text-[#0A2540] dark:text-white">{p.label}</span>
                      {property === p.id && <div className="w-3 h-3 rounded-full bg-[#C9A356]"></div>}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(2)} className="btn-outline-navy flex-1" data-testid="calc-back-3">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </button>
                  <button onClick={() => setStep(4)} className="btn-accent flex-1" data-testid="calc-next-3">
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="calc-step-4-result" className="fade-in-up text-center">
                <div className="w-16 h-16 rounded-full bg-[#FFFBF0] dark:bg-[#3a2e16] grid place-items-center mx-auto mb-5 border-2 border-[#C9A356]/40">
                  <CheckCircle2 className="w-8 h-8 text-[#C9A356]" />
                </div>
                <p className="overline mb-2">Information received</p>
                <h3 className="font-heading text-2xl text-[#0A2540] dark:text-white font-medium">
                  Thanks — let's review your numbers together.
                </h3>
                <p className="text-[#475569] dark:text-slate-400 mt-3 leading-relaxed max-w-md mx-auto">
                  We've got everything we need to prepare your personalized estimate.
                  Schedule a free consultation and we'll walk you through projected savings,
                  system sizing and pricing options in person.
                </p>

                <div className="mt-8 card-pb p-5 bg-[#0A2540] text-white border-0 text-left">
                  <div className="flex items-start gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-[#C9A356] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium">Next step: book your appointment</div>
                      <p className="text-sm text-slate-300 mt-1">
                        Free site visit · 30-45 minutes · in-person or video call
                      </p>
                    </div>
                  </div>
                  <button onClick={goToSignup} className="btn-accent w-full" data-testid="calc-signup-cta">
                    Schedule my consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                  <button
                    onClick={() => { setStep(1); }}
                    className="w-full text-xs text-slate-300 hover:text-white mt-3"
                    data-testid="calc-restart"
                  >
                    ← Edit my answers
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
